import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { baseHref } from "service/api";
import { Crypto } from "types/crypto";
import { Fiat } from "types/fiat";

type ExchangeCurrenciesResponse = {
  data?: { crypto: Crypto[]; fiat: Fiat[] };
  result: boolean;
};

type SwapCurrenciesResponse = {
  data?: { crypto: Crypto[] };
  result: boolean;
};

type ExchangeRatesResponse = {
  data?: {
    amount_crypto: number;
    amount_fiat: number;
    ask_fee_of_rate: string;
    ask_real: string;
    ask_with_fee: string;
    bid_fee_of_rate: string;
    bid_real: string;
    bid_with_fee: string;
    company_fee_rate_percent: string;
    created_at: string;
    exchange_type: string;
    id: number;
    login: string;
    symbol: string;
    timeExpiration: number;
    timeRateExpiration: number;
    company_fee: string;
    updated_at: string;
  }[];
  result: boolean;
};

type CryptoToFiatCalculateResponse = {
  amount: string;
  amountCryptoNet: string;
  systemFee: number;
  companyFeeRatePercent: string;
  amountCryptoFee: string;
  result: boolean;
};

type ExchangeCalculateResponse = {
  amount: string;
  systemFee: number;
  companyFeeRatePercent: number;
  result: boolean;
};

type SwapCalculateResponse = {
  amount: string;
  systemFee: number;
  companyFeeRatePercent: number;
  amountCryptoFromNet: string;
  amountCryptoFee: string;
  amountCryptoFromOriginal: string;
  amountCryptoToOriginal: string;
  result: boolean;
};

type ExchangeBuyResponse = {
  operationId?: number;
  result: boolean;
  redirectUrl?: string | null;
  transaction_id?: number;
  merchantOrderId?: string;
  status?: string;
};

type ExchangeSellResponse = {
  operationId: number;
  result: boolean;
};

type InitSwapResponse = {
  operationId: number;
  result: boolean;
};

type ExchangeSellConfirmResponse = {
  redirectUrl?: string | null;
  transactionId: number;
  result: boolean;
  status?: string;
  error?: string;
};

type ExchangeFiatToCryptoConfirmResponse = {
  result: boolean;
};

type ExchangeEffectiveUserCryptoBalanceResponse = {
  balance: number;
  result: boolean;
};
export const exchangeApi = createApi({
  reducerPath: "exchangeApi",
  baseQuery: fetchBaseQuery({
    baseUrl: `${baseHref}/exchange`,
    credentials: "include",
  }),
  endpoints: (builder) => ({
    swapCurrencies: builder.query<SwapCurrenciesResponse, void>({
      query: () => `currencies-for-swap`,
    }),
    exchangeCurrencies: builder.query<ExchangeCurrenciesResponse, void>({
      query: () => `currencies`,
    }),
    exchangeRates: builder.query<
      ExchangeRatesResponse,
      { crypto: Crypto; transactionType: "buy" | "sell" }
    >({
      query: ({ crypto, transactionType }) =>
        `rates/${crypto}/transactionType/${transactionType}`,
    }),
    exchangeRatesForSwap: builder.query<ExchangeRatesResponse, Crypto>({
      query: (crypto) => `rates-for-swap/${crypto}`,
    }),
    exchangeCalculate: builder.query<
      ExchangeCalculateResponse,
      Record<string, string>
    >({
      query: (payload) => ({
        url: `calculate`,
        method: "GET",
        params: payload,
      }),
    }),
    cryptoToFiatCalculate: builder.query<
      CryptoToFiatCalculateResponse,
      Record<string, string>
    >({
      query: (payload) => ({
        url: `crypto-to-fiat-calculate`,
        method: "GET",
        params: payload,
      }),
    }),
    swapCalculate: builder.query<SwapCalculateResponse, Record<string, string>>(
      {
        query: (payload) => ({
          url: `swap-calculate`,
          method: "GET",
          params: payload,
        }),
      }
    ),
    exchangeBuy: builder.mutation<ExchangeBuyResponse, Record<string, any>>({
      query: (payload) => ({
        url: `buy`,
        method: "POST",
        body: payload,
      }),
    }),
    initSwap: builder.mutation<InitSwapResponse, Record<string, any>>({
      query: (payload) => ({
        url: `init-swap`,
        method: "POST",
        body: payload,
      }),
    }),
    confirmSwap: builder.mutation<
      ExchangeSellConfirmResponse,
      Record<string, any>
    >({
      query: (payload) => ({
        url: `iledgends-crypto-to-crypto`,
        method: "POST",
        body: payload,
      }),
    }),
    exchangeSell: builder.mutation<ExchangeSellResponse, Record<string, any>>({
      query: (payload) => ({
        url: `sell`,
        method: "POST",
        body: payload,
      }),
    }),
    exchangeSellConfirm: builder.mutation<
      ExchangeSellConfirmResponse,
      Record<string, any>
    >({
      query: (payload) => ({
        url: `confirm-sell`,
        method: "POST",
        body: payload,
      }),
    }),
    exchangeCryptoToFiatConfirm: builder.mutation<
      ExchangeSellConfirmResponse,
      Record<string, any>
    >({
      query: (payload) => ({
        url: `iledgends-crypto-to-fiat`,
        method: "POST",
        body: payload,
      }),
    }),
    exchangeFiatToCryptoConfirm: builder.mutation<
      ExchangeFiatToCryptoConfirmResponse,
      Record<string, any>
    >({
      query: (payload) => ({
        url: `iledgends-fiat-to-crypto`,
        method: "POST",
        body: payload,
      }),
    }),
    exchangeEffectiveUserCryptoBalance: builder.query<
      ExchangeEffectiveUserCryptoBalanceResponse,
      Crypto
    >({
      query: (crypto) =>
        `get-effective-user-crypto-balance/${crypto.toUpperCase()}`,
    }),
  }),
});

export const {
  useExchangeCurrenciesQuery,
  useSwapCurrenciesQuery,
  useExchangeRatesQuery,
  useExchangeRatesForSwapQuery,
  useLazyExchangeCalculateQuery,
  useLazySwapCalculateQuery,
  useExchangeBuyMutation,
  useExchangeSellMutation,
  useExchangeSellConfirmMutation,
  useExchangeCryptoToFiatConfirmMutation,
  useExchangeFiatToCryptoConfirmMutation,
  useLazyExchangeEffectiveUserCryptoBalanceQuery,
  useInitSwapMutation,
  useConfirmSwapMutation,
  useLazyCryptoToFiatCalculateQuery,
} = exchangeApi;
